import { UploadImageResp } from "@humanex/interfaces";
import { cn } from "@humanex/ui/libs";
import { Image as ImageIcon, X } from "lucide-react";
import * as React from "react";

import { useUploadImage } from "../../../hooks";

interface ImageInputProps {
  /** Current image URL value */
  value?: string | null;
  /** Callback when image URL changes */
  onChange: (url: string) => void;
  /** Function to upload image file */
  uploadFn: (file: File) => Promise<UploadImageResp>;
  /** Label text */
  label?: string;
  required?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Custom className for container */
  className?: string;
  /** Custom className for label */
  labelClassName?: string;
  /** Custom className for preview image container */
  previewClassName?: string;
  /** Custom className for upload area */
  uploadAreaClassName?: string;
  /** Preview image dimensions - default: "w-32 h-32" */
  previewSize?: string;
  /** Maximum file size in bytes - default: 5MB */
  maxSize?: number;
  /** Error message to display */
  error?: string;
  /** Custom invalid image type message */
  invalidImageTypeMessage?: string;
  /** Custom file too large message */
  fileTooLargeMessage?: string;
  /** Optional toast.success function for success notifications */
  toastSuccess?: (message: string) => void;
  /** Optional toast.error function for error notifications */
  toastError?: (message: string) => void;
  /** Custom success message for toast */
  successMessage?: string;
  /** Custom error message for toast */
  errorMessage?: string;
  /** Alt text for preview image */
  alt?: string;
  /** Accept attribute for file input - default: "image/*" */
  accept?: string;
  /** Callback when upload starts */
  onUploadStart?: () => void;
  /** Callback when upload succeeds */
  onUploadSuccess?: (url: string) => void;
  /** Callback when upload fails */
  onUploadError?: (error: Error) => void;
}

export const ImageInput = React.forwardRef<HTMLInputElement, ImageInputProps>(
  (
    {
      value,
      onChange,
      uploadFn,
      label,
      required,
      disabled = false,
      className,
      labelClassName,
      previewClassName,
      uploadAreaClassName,
      previewSize = "w-32 h-32",
      maxSize = 5 * 1024 * 1024, // 5MB default
      error,
      invalidImageTypeMessage,
      fileTooLargeMessage,
      toastSuccess,
      toastError,
      successMessage,
      errorMessage,
      alt = "Image preview",
      accept = "image/*",
      onUploadStart,
      onUploadSuccess,
      onUploadError,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = React.useState(false);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const { uploadImage } = useUploadImage({
      onSuccess: (url) => {
        onChange(url);
        onUploadSuccess?.(url);
        if (successMessage && toastSuccess) {
          toastSuccess(successMessage);
        }
      },
      onError: (err) => {
        onUploadError?.(err);
        if (errorMessage && toastError) {
          toastError(errorMessage);
        }
      }
    });

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        const errorMsg = invalidImageTypeMessage || "Invalid image type";
        const error = new Error(errorMsg);
        onUploadError?.(error);
        if (toastError && invalidImageTypeMessage) {
          toastError(invalidImageTypeMessage);
        }
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        const errorMsg = fileTooLargeMessage || "File too large";
        const error = new Error(errorMsg);
        onUploadError?.(error);
        if (toastError && fileTooLargeMessage) {
          toastError(fileTooLargeMessage);
        }
        return;
      }
      setIsUploading(true);
      onUploadStart?.();

      try {
        await uploadImage(file, uploadFn);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Upload failed");
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
        // Reset input to allow selecting the same file again
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    };

    const handleRemove = () => {
      onChange("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    const handleUploadAreaClick = () => {
      if (!disabled && !isUploading) {
        inputRef.current?.click();
      }
    };

    return (
      <div className={cn(className)}>
        {label && (
          <div className={cn("text-sm font-medium text-ink mb-1", labelClassName)}>
            {label} {required && <span className="text-error-500">(*)</span>}
          </div>
        )}
        {value ? (
          <div className={cn("relative inline-block", previewClassName)}>
            <img
              src={value}
              alt={alt}
              className={cn(previewSize, "object-cover rounded-lg border border-gray-200")}
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              disabled={disabled || isUploading}
              aria-label="Remove image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            onClick={handleUploadAreaClick}
            className={cn(
              previewSize,
              "rounded-lg border-2 border-dashed border-gray-200 hover:border-secondary-400 transition-colors flex flex-col items-center justify-center cursor-pointer",
              (disabled || isUploading) && "opacity-40 pointer-events-none",
              uploadAreaClassName
            )}
          >
            {isUploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-secondary-400"></div>
            ) : (
              <>
                <ImageIcon className="h-6 w-6 text-gray-500 mb-2" />
                <span className="text-xs text-gray-500">Upload image</span>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled || isUploading}
          {...props}
        />
        {error && <p className="text-xs text-error-500 mt-1">{error}</p>}
      </div>
    );
  }
);

ImageInput.displayName = "ImageInput";
